import {useEffect, useMemo, useState} from 'react';
import {getReportHeadersByIds} from '../firebase/firestore/reports';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

function getClaimReportId(claim) {
    return claim.reportId || claim.report_id || null;
}

/**
 * Fetch claims for an entity (by entityId / wikidataId).
 * Phase 6 replacement for Firestore `claims` collection subscription.
 *
 * For draft claims, only exposes claims belonging to reports the current user
 * can access — access is determined by whether /api/reports?ids=... returns
 * a header for that report (the endpoint enforces server-side access control).
 * Loading stays true until both the claims fetch AND the headers fetch complete
 * so the tab count reflects only accessible claims.
 */
export function useEntityClaims(entityId, mode = 'fintel') {
    const [rawClaims, setRawClaims] = useState([]);
    const [reportHeadersById, setReportHeadersById] = useState({});
    const [claimsLoading, setClaimsLoading] = useState(!!entityId);
    const [headersLoading, setHeadersLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!entityId) { setRawClaims([]); setClaimsLoading(false); setError(null); return; }

        let cancelled = false;
        setClaimsLoading(true); setError(null);

        const status = (mode === 'draft') ? 'draft' : 'approved';
        fetch(
            `${BASE}/api/claims?entityId=${encodeURIComponent(entityId)}&status=${encodeURIComponent(status)}`,
            {headers: authHeaders()}
        )
            .then((r) => r.ok ? r.json() : [])
            .then((data) => { if (!cancelled) { setRawClaims(Array.isArray(data) ? data : []); setClaimsLoading(false); } })
            .catch((err) => { if (!cancelled) { setError(err); setClaimsLoading(false); } });

        return () => { cancelled = true; };
    }, [entityId, mode]);

    useEffect(() => {
        const ids = [...new Set(rawClaims.map(getClaimReportId).filter(Boolean))];
        if (!ids.length) { setReportHeadersById({}); setHeadersLoading(false); return; }
        let cancelled = false;
        setHeadersLoading(true);
        getReportHeadersByIds(ids)
            .then((h) => { if (!cancelled) { setReportHeadersById(h || {}); setHeadersLoading(false); } })
            .catch(() => { if (!cancelled) { setReportHeadersById({}); setHeadersLoading(false); } });
        return () => { cancelled = true; };
    }, [rawClaims]);

    const loading = claimsLoading || headersLoading;

    // For draft mode: filter to only claims for reports the current user can access.
    // Access is confirmed by the presence of a report header returned from the server.
    // While still loading, return all raw claims so intermediate renders are stable.
    const claims = useMemo(() => {
        if (mode !== 'draft' || claimsLoading || headersLoading) return rawClaims;
        const accessibleIds = new Set(Object.keys(reportHeadersById));
        return rawClaims.filter(c => {
            const rId = getClaimReportId(c);
            return !rId || accessibleIds.has(rId);
        });
    }, [rawClaims, reportHeadersById, mode, claimsLoading, headersLoading]);

    return {claims, reportHeadersById, loading, error};
}
